import React from 'react';
import PropTypes from "prop-types";
import classNames from "classnames";
import { default as ReactSelect, components } from 'react-select';
import Async from 'react-select/async';
import Creatable from 'react-select/creatable';
import AsyncCreatable from 'react-select/async-creatable';
// import Icon from "../components/Icon";
import { Icon } from "rhombus-react";

const ClearIndicator = (props) => {
	return (components.ClearIndicator && (
		<components.ClearIndicator {...props}>
			<Icon name="clear" />
		</components.ClearIndicator>
	));
};

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<Icon name={props.selectProps.menuIsOpen ? "caretUp" : "caretDown"} />
		</components.DropdownIndicator>
	);
};

export default class Select extends React.Component {

	static displayName = 'Select';

	static defaultProps = {
		isClearable:         true,
		isMulti:             false,
		isDisabled:          false,
		optionClassName:     "e-form__select-option",
		options:             [],
		placeholder:         "Select...",
		classNamePrefix:     "react-select",
		theme:               "primary",
		className:           "",
		allowCreate:         false,
		isAsync:             false,
		autoload:            false,
		preserveSubObjects:  true,
		onSelectResetsInput: true,
		components:          {},
		pre:                 null,
		post:                null,
	};

	// See https://github.com/JedWatson/react-select for additional prop options
	// 		latest react-select docs:	https://react-select.com/props
	// 		v1 -> v2 upgrade guide:		https://react-select.com/upgrade-guide
	// 		v2 -> v3 upgrade guide:		https://github.com/JedWatson/react-select/issues/3585
	static propTypes = {
		/**	Can we clear the value -- [[ may be passed in as clearable in older code, allowed for backward compatibility ]] */
		isClearable: PropTypes.bool,
		/** Is this a multi-value input -- [[ may be passed in as multi in older code, allowed for backward compatibility ]] */
		isMulti: PropTypes.bool,
		/** is this component disabled? -- [[ may be passed in as disabled, allowed for backward compatibility ]] */
		isDisabled: PropTypes.bool,
		/**	Class name to add to the options rendered component */
		optionClassName: PropTypes.string,
		/**	Options that the select should show */
		options: PropTypes.array,
		/** placeholder text */
		placeholder: PropTypes.string,
		/** prefix to use for class names under the main select so we can target those elements */
		classNamePrefix: PropTypes.string,
		/** */
		theme: PropTypes.string,
		/** */
		className: PropTypes.string,
		/**	should this be a Creatable select? */
		allowCreate: PropTypes.bool,
		/** Should we use the Async version of the React.Select */
		isAsync: PropTypes.bool,
		/** :: Not prefaced with "is" because it's native to React-Select */
		autoload: PropTypes.bool,
		/** */
		preserveSubObjects: PropTypes.bool,
		/** this was a prop in react-select 1.x that was removed in react-select 2.x; this is now handled in ControlWrapper.js handleInputChange() */
		onSelectResetsInput: PropTypes.bool,	// see ControlWrapper.js handleInputChange()
		/** component renderers (see react-select v3 components guide: https://react-select.com/components) */
		components: PropTypes.object,
		/** custom validator to ensure we add onMenuScrollToBottom functions to Selects */
		onMenuScrollToBottom(props, propName, componentName){
			let fieldName = props.label || props.name || props.className
				, fieldInfo = (fieldName) ? ` for ${fieldName} Field` : null
			;

			if(!props.onMenuScrollToBottom){
				return new Error(`<${componentName}>${fieldInfo} has no ${propName} function.  This will mean no API calls happen when users scroll.  If this is intentional please pass in noop for onMenuScrollToBottom.`);
			}
		},

		/** Content to display inline with the `input` element, inside the border
		 * similar to the `icon` prop, but more flexible.
		 * `pre` is added before the input element.
		 * `post` is added after the input element.
		*/
		pre: PropTypes.node,
		post: PropTypes.node,
	};

	constructor(props, context){
		super(props, context);

		let setState = { value: null };

		if(props.value){
			if(props.multi || props.isMulti){
				setState = { value: props.value };
			}else{
				if(props.value.label || props.value.value){
					setState = { _length: props.value.value, value: props.value };
				}
			}
		}

		this.state = setState;

		this._buildOption = this._buildOption.bind(this);
		this._buildSingleValue = this._buildSingleValue.bind(this);
		this._buildSelectProps = this._buildSelectProps.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
	}

	componentWillReceiveProps(nextProps){
		var { props } = this;

		if(nextProps.value !== props.value && nextProps.value !== this.state.value) {
			if(nextProps.value){
				if(props.multi || props.isMulti){
					this.setState({ value: nextProps.value });
				}else{
					if(nextProps.value.label || nextProps.value.value){
						this.setState({ value: nextProps.value });
					}
				}
			} else {
				this.setState({ value: null });
			}
		}
	}

	/* optionRenderer was removed in the upgrade from react-select v1.x -> v2.x
	 * instead, we need to pass in an Option component
	 * convert optionRenderer to an Option component here for backward compatability
	 */
	_buildOption(props){	// option props from react-select
		let { optionRenderer } = this.props;	//this component's props

		return (components.Option && (
			<components.Option {...props}>
				{optionRenderer(props.data)}
			</components.Option>
		));
	}

	/* valueRenderer was removed in the upgrade from react-select v1.x -> v2.x
	 * instead, we need to pass in a SingleValue component
	 * convert valueRenderer to a SingleValue component here for backward compatability
	 */
	_buildSingleValue(props){	// value props from react-select
		let { valueRenderer } = this.props;	//this component's props

		return (components.Option && (
			<components.SingleValue {...props}>
				{valueRenderer(props.data)}
			</components.SingleValue>
		));
	}

	_buildSelectProps(props){
		let { state } = this
			, selectProps = {
				...props,
				value:     state.value,
				onChange:  this.onChange,
				className: `react-select value-color--${props.theme}`,
				// In order for react-select to work with redux-form, we need to pass in onBlur
				// https://github.com/erikras/redux-form/issues/82#issuecomment-274047138
				onBlur: this.onBlur,
				// react-select v3 components guide: https://react-select.com/components
				components: {
					ClearIndicator,
					DropdownIndicator,
					...props.components,
				},
			}
			;

		// some props were renamed after react-select 1.x (using 3.x as of 7/2/19)
		// if they were passed in the old way, assign them here (so we don't have to update old code)
		// NOTE: onSelectResetsInput was removed in 2.x and we now handle it in ControlWrapper.js handleInputChange()
		// NOTE: if breaking changes are encountered, to determine if a prop name has changed or been removed check
		// 		latest react-select docs:	https://react-select.com/props
		// 		v1 -> v2 upgrade guide:		https://react-select.com/upgrade-guide
		// 		v2 -> v3 upgrade guide:		https://github.com/JedWatson/react-select/issues/3585
		if(typeof(props.clearable)      === "boolean"){ selectProps.isClearable = props.clearable; }
		if(typeof(props.closeOnSelect)  === "boolean"){ selectProps.closeMenuOnSelect = props.closeOnSelect; }
		if(typeof(props.disabled)       === "boolean"){ selectProps.isDisabled = props.disabled; }
		if(typeof(props.multi)          === "boolean"){ selectProps.isMulti = props.multi; }
		if(typeof(props.onKeyDown)      === "function"){ selectProps.onInputKeyDown = props.onKeyDown; }
		if(typeof(props.searchable)     === "boolean"){ selectProps.isSearchable = props.searchable; }
		if(typeof(props.optionRenderer) === "function"){ selectProps.components.Option = this._buildOption; }
		if(typeof(props.valueRenderer)  === "function"){ selectProps.components.SingleValue = this._buildSingleValue; }

		return selectProps;
	}

	onChange(value){
		var { props, state } = this
			, x = null
			, newValue = null
			;

		if(value !== null){
			if(!value && props.preserveSubObjects) {
				newValue = {};
				for(x in state.value) {
					if(state.value[x] && typeof(state.value[x]) === "object") {
						newValue[x] = state.value[x];
					}
				}
			} else {
				newValue = value;
			}	//end if(props.perserveSubObjects)
		}	//end if(value !== null)

		this.setState({ _length: (value && value.label) || 0, value: newValue }, () => {
			if(props.onChange) {
				props.onChange(value);
			}
		});
	}

	onBlur(){
		var currentValue = this.state.value;

		if(this.props.onBlur) {
			this.props.onBlur(currentValue);
		}
	}

	render(){
		let {
			  allowCreate,
			  isAsync,
			  icon,
			  pre,
			  post,
			  ...props
			} = this.props
			, selectProps = this._buildSelectProps(props)
			, $node = null
			, hasIcon = this.props.showIcon && this.props.icon
			, divClasses = {
				"e-form__control":              true,
				"e-form__control-react-select": true,
				"react-select-holder":          true,
				"has-icon":                     hasIcon || pre || post,
				"has-clear":                    this.state.value && selectProps.isClearable,
			}
			;

		if (props.multi || props.isMulti) {
			function removeItem(event, props) {
				event.stopPropagation();
				props.onRemove(props.value);
			}
			selectProps.valueComponent = (props) => {
				return (
					<div className="Select-value">
						<span className="Select-value-label" role="option" aria-selected="true">
							{props.value.label}
						</span>
						<Icon
							name="clearCircle"
							className="rhombus-multiselect-remove"
							aria-hidden="true"
							onClick={e => removeItem(e, props)}
							onMouseDown={e => e.stopPropagation()}
						/>
					</div>
				);
			};
		}

		if(allowCreate){
			if(isAsync){
				$node = <AsyncCreatable {...selectProps} />;
			} else {
				$node = <Creatable {...selectProps} />;
			}
		} else {
			if(isAsync){
				$node = <Async {...selectProps} />;
			} else {
				$node = <ReactSelect {...selectProps} />;
			}
		}

		return (
			<div className={classNames(divClasses)}>
				{hasIcon ? <Icon className="e-form__control--icon e-form__control--icon-left search-icon" name={icon} /> : null}
				{ pre ? <div className="e-form__control--icon e-form__control--icon-left inline-content pre-content">{pre}</div> : null }
				{$node}
				{ post ? <div className="inline-content post-content">{post}</div> : null }
			</div>
		);
	}

}
